import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Tag } from "@/components/Layout";
import { assetConditionTone } from "@/lib/statusTone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { Plus, ImagePlus, Loader2, Trash2, X, Pencil, Search } from "lucide-react";
import { toast } from "sonner";
import BulkUploadDialog from "@/components/BulkUploadDialog";
import FinanceEntryFields, { isFinanceEntryValid } from "@/components/FinanceEntryFields";
import { formatDate } from "@/lib/dateFormat";
import useBulkSelection from "@/hooks/useBulkSelection";
import usePermissions from "@/lib/usePermissions";

const emptyForm = () => ({
  name: "", category: "Equipment", serial: "", model_number: "",
  assigned_to: "", condition: "good", purchased_at: "", purchase_amount: 0,
  notes: "", images: [],
  // Shared Finance Entry field set — Assets uses finance_category/finance_sub_category as the canonical pair.
  project_id: "", department: "",
  finance_category: "", finance_sub_category: "", cost_type: "", aop: "",
  vendor_id: "", vendor: "", payment_mode: "", source_id: "",
  drive_link: "", month: new Date().toISOString().slice(0, 7),
});

export default function Assets() {
  const { user } = useAuth();
  const can = usePermissions();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [financeCats, setFinanceCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [search, setSearch] = useState("");
  const [assignedFilter, setAssignedFilter] = useState("__all"); // admin-only: __all | __unassigned | <user id>
  const fileRef = useRef(null);

  const load = async () => {
    const [a, u, p, c] = await Promise.all([
      api.get("/assets"),
      api.get("/users", { params: { kind: "internal" } }),
      api.get("/projects").catch(() => ({ data: [] })),
      api.get("/finance/categories").catch(() => ({ data: { categories: [] } })),
    ]);
    setRows(a.data);
    setUsers(u.data);
    setProjects(p.data || []);
    setFinanceCats(c.data?.categories || []);
  };
  useEffect(() => { load(); }, []);

  const canCreate = can("assets_create");
  const canEdit = can("assets_edit");
  const canDelete = can("assets_delete");
  const isAdmin = canCreate || canEdit || canDelete;

  const bulk = useBulkSelection({
    collection: "assets",
    ids: rows.map((r) => r.id),
    onDone: load,
  });
  const usersMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const internalUsers = useMemo(() => users.filter((u) => u.role !== "client"), [users]);
  const projectsMap = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);

  const subOptions = useMemo(
    () => financeCats.find((c) => c.category === form.finance_category)?.sub_categories || [],
    [financeCats, form.finance_category],
  );

  // Real-time filter — name / project / category / sub-category / assignee.
  const filteredRows = useMemo(() => {
    let base = rows;
    if (assignedFilter === "__unassigned") {
      base = base.filter((r) => !r.assigned_to);
    } else if (assignedFilter !== "__all") {
      base = base.filter((r) => r.assigned_to === assignedFilter);
    }
    const q = search.trim().toLowerCase();
    if (!q) return base;
    return base.filter((r) => {
      const proj = projectsMap[r.project_id]?.name || "";
      const assignee = usersMap[r.assigned_to]?.name || "";
      return [
        r.name,
        r.category,
        proj,
        assignee,
        r.finance_category,
        r.finance_sub_category,
        r.serial,
        r.model_number,
      ].some((v) => (v || "").toString().toLowerCase().includes(q));
    });
  }, [rows, search, assignedFilter, projectsMap, usersMap]);

  const startCreate = () => { setEditing(null); setForm(emptyForm()); setOpen(true); };
  const startEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name || "", category: r.category || "Equipment", serial: r.serial || "",
      model_number: r.model_number || "", assigned_to: r.assigned_to || "",
      condition: r.condition || "good", purchased_at: r.purchased_at || "",
      purchase_amount: r.purchase_amount || 0, notes: r.notes || "",
      images: r.images || [],
      project_id: r.project_id || "",
      finance_category: r.finance_category || "",
      finance_sub_category: r.finance_sub_category || "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form, purchase_amount: Number(form.purchase_amount) || 0 };
      if (editing) {
        await api.patch(`/assets/${editing.id}`, payload);
        toast.success("Asset updated");
      } else {
        const endpoint = canCreate ? "/assets" : "/assets/self";
        await api.post(endpoint, payload);
        toast.success(canCreate && payload.purchase_amount && payload.purchased_at ? "Asset added + expense auto-logged in Finance" : "Asset added");
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyForm());
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete asset "${r.name}"? The linked expense entry will also be removed.`)) return;
    try {
      await api.delete(`/assets/${r.id}`);
      toast.success("Asset removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if ((form.images || []).length >= 5) {
      toast.error("Max 5 images per asset");
      return;
    }
    setUploadingIdx((form.images || []).length);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/asset-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...(f.images || []), data.url] }));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingIdx(null); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeImage = (idx) => {
    setForm((f) => ({ ...f, images: (f.images || []).filter((_, i) => i !== idx) }));
  };

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto fade-in" data-testid="assets-page">
      <PageHeader
        eyebrow="Register"
        title="Asset register"
        subtitle="Track every laptop, printer, tool and license. Photos, purchase dates and amounts auto-sync to the Finance expenses tab."
        actions={(
          <div className="inline-flex items-center gap-2 flex-wrap justify-end">
            <BulkUploadDialog section="assets" label="Assets" onCommitted={load} />
            {/* Phase 87 — Add Asset is visible to anyone who reached this page
                (page access is already gated by the "my_hr_assets" permission
                at the MyHR tab level), not just Admin/Founder. */}
            <Button onClick={startCreate} data-testid="new-asset-btn" className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white">
              <Plus size={14} className="mr-1.5" />Add asset
            </Button>
          </div>
        )}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            placeholder="Search assets, projects, categories, sub-categories, assignees…"
            className="rounded-sm pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="assets-search"
          />
        </div>
        {isAdmin && (
          <Select value={assignedFilter} onValueChange={setAssignedFilter}>
            <SelectTrigger className="rounded-sm w-56" data-testid="assets-assignee-filter">
              <SelectValue placeholder="Assigned to · everyone" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Assigned to · everyone</SelectItem>
              <SelectItem value="__unassigned">Unassigned only</SelectItem>
              {internalUsers.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name} {u.business_unit ? `· ${u.business_unit}` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="card-flat overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-[1100px]">
            <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-200 label-overline">
              {bulk.canBulk && (
                <div className="w-6 flex-shrink-0">
                  <bulk.HeaderCheckbox />
                </div>
              )}
              <div className="grid gap-3 flex-1" style={{ gridTemplateColumns: "minmax(0,3fr) minmax(0,1.5fr) minmax(0,2fr) minmax(0,1.5fr) minmax(0,1.5fr) minmax(0,1.5fr) minmax(0,1.6fr) minmax(0,90px)" }}>
                <div>Asset</div>
                <div>Category</div>
                <div>Project</div>
                <div>Fin. category</div>
                <div>Sub-category</div>
                <div>Assignee</div>
                <div>Purchased</div>
                <div className="text-right">Actions</div>
              </div>
            </div>
            <div className="divide-row">
              {filteredRows.map((r) => (
            <div key={r.id} className={`flex items-center gap-3 px-5 py-3 text-sm hover-row ${bulk.isSelected(r.id) ? "bg-orange-50/40" : ""}`} data-testid={`asset-row-${r.id}`}>
              {bulk.canBulk && (
                <div className="w-6 flex-shrink-0">
                  <bulk.RowCheckbox id={r.id} />
                </div>
              )}
              <div className="grid gap-3 flex-1 items-center" style={{ gridTemplateColumns: "minmax(0,3fr) minmax(0,1.5fr) minmax(0,2fr) minmax(0,1.5fr) minmax(0,1.5fr) minmax(0,1.5fr) minmax(0,1.6fr) minmax(0,90px)" }}>
              <div className="flex items-center gap-3 min-w-0">
                {r.images?.[0] && (
                  <img src={r.images[0]} alt="" className="w-12 h-12 object-cover rounded-sm border border-slate-200 cursor-pointer flex-shrink-0" onClick={() => startEdit(r)} />
                )}
                <div className="min-w-0">
                  <div className="font-medium text-slate-800 truncate">{r.name}</div>
                  <div className="text-[10px] text-slate-400 truncate">{(r.images || []).length} img · <Tag tone={assetConditionTone(r.condition)}>{(r.condition || "").replace("_", " ")}</Tag></div>
                </div>
              </div>
              <div className="text-slate-700 truncate">{r.category}</div>
              <div className="text-slate-700 truncate">{projectsMap[r.project_id]?.name || <span className="text-slate-400">—</span>}</div>
              <div className="text-slate-700 truncate">{r.finance_category || <span className="text-slate-400">—</span>}</div>
              <div className="text-slate-500 truncate">{r.finance_sub_category || <span className="text-slate-400">—</span>}</div>
              <div className="text-slate-700 truncate">{usersMap[r.assigned_to]?.name || "Unassigned"}</div>
              <div className="min-w-0">
                {r.purchased_at ? (
                  <>
                    <div className="text-slate-700">{formatDate(r.purchased_at, { fallback: "" })}</div>
                    {r.purchase_amount ? <div className="text-[11px] text-slate-500">₹{Number(r.purchase_amount).toLocaleString("en-IN")}</div> : null}
                  </>
                ) : <span className="text-slate-400">—</span>}
              </div>
              <div className="text-right inline-flex items-center justify-end gap-1">
                {canEdit && <Button size="sm" variant="outline" className="rounded-sm h-7 px-2" onClick={() => startEdit(r)} data-testid={`edit-asset-${r.id}`}><Pencil size={12} /></Button>}
                {canDelete && <Button size="sm" variant="outline" className="rounded-sm h-7 px-2 text-red-700 border-red-200 hover:bg-red-50" onClick={() => remove(r)} data-testid={`delete-asset-${r.id}`}><Trash2 size={12} /></Button>}
              </div>
              </div>
            </div>
          ))}
          {filteredRows.length === 0 && (
            <div className="p-5 text-sm text-slate-500">{search ? "No assets match your search." : "No assets yet."}</div>
          )}
            </div>
          </div>
        </div>
      </div>
      <bulk.ActionBar />
      <bulk.ConfirmDialog />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit asset" : "Add asset"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2 max-h-[70vh] overflow-y-auto pr-1">
            <div><Label className="label-overline">Name</Label><Input className="rounded-sm mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="asset-name" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label className="label-overline">Category</Label><Input className="rounded-sm mt-1" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
              <div><Label className="label-overline">Serial #</Label><Input className="rounded-sm mt-1" value={form.serial} onChange={(e) => setForm({ ...form, serial: e.target.value })} /></div>
              <div><Label className="label-overline">Model #</Label><Input className="rounded-sm mt-1" value={form.model_number} onChange={(e) => setForm({ ...form, model_number: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-overline">Assigned to</Label>
                <Select value={form.assigned_to || "none"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "none" ? "" : v })}>
                  <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {internalUsers.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-overline">Condition</Label>
                <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                  <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="good">Good</SelectItem>
                    <SelectItem value="needs_repair">Needs repair</SelectItem>
                    <SelectItem value="retired">Retired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-overline">Purchase date</Label>
                <Input type="date" className="rounded-sm mt-1" value={form.purchased_at} onChange={(e) => setForm({ ...form, purchased_at: e.target.value })} data-testid="asset-purchase-date" />
              </div>
              <div>
                <Label className="label-overline">Purchase amount (₹)</Label>
                <Input type="number" className="rounded-sm mt-1" value={form.purchase_amount} onChange={(e) => setForm({ ...form, purchase_amount: e.target.value })} data-testid="asset-purchase-amount" />
              </div>
            </div>
            <p className="text-[11px] text-slate-500 italic">Both date + amount? We&apos;ll auto-create a matching expense in Finance with every Finance Entry field copied 1:1.</p>

            {/* Phase 44 — Shared Finance Entry fields (Project + Department + Category + Sub + Cost Type + AOP + Vendor + Pay Mode + Source + Drive + Month) */}
            <div className="pt-3 border-t border-slate-200">
              <FinanceEntryFields
                value={form}
                onChange={(p) => setForm({ ...form, ...p })}
                testidPrefix="asset"
                categoryKey="finance_category"
                subCategoryKey="finance_sub_category"
              />
            </div>

            <div>
              <Label className="label-overline">Photos · {(form.images || []).length}/5</Label>
              <div className="mt-2 grid grid-cols-5 gap-2">
                {(form.images || []).map((u, i) => (
                  <div key={u} className="relative group" data-testid={`asset-image-${i}`}>
                    <img src={u} alt="" className="w-full aspect-square object-cover rounded-sm border border-slate-200" />
                    <button onClick={() => removeImage(i)} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"><X size={10} /></button>
                  </div>
                ))}
                {(form.images || []).length < 5 && (
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploadingIdx !== null}
                    className="aspect-square border-2 border-dashed border-slate-300 rounded-sm flex flex-col items-center justify-center text-slate-400 hover:border-[#F54800] hover:text-[#F54800] transition-colors"
                    data-testid="asset-add-image"
                  >
                    {uploadingIdx !== null ? <Loader2 size={20} className="animate-spin" /> : <ImagePlus size={20} />}
                    <span className="text-[10px] mt-1">Add</span>
                  </button>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadImage} />
            </div>

            <div><Label className="label-overline">Notes</Label><Textarea rows={2} className="rounded-sm mt-1" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="asset-save">{editing ? "Save" : "Add"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
